export function getExtensionFromUrl(url: string): string | null {
  try {
    const pathname = new URL(url).pathname;
    const lastSegment = pathname.split('/').pop() || '';
    const lastDot = lastSegment.lastIndexOf('.');
    if (lastDot === -1 || lastDot === lastSegment.length - 1) return null;
    return lastSegment.slice(lastDot + 1) || null;
  } catch {
    return null;
  }
}

export type DownloadLinkItem = { name?: string | null; url?: string | null };

export type DownloadItemsFetchers = {
  getFileById: (id: number) => Promise<{ success?: boolean; data?: { name?: string; versions?: Array<{ name?: string | null; fileUrl?: string | null }> } }>;
  /** API'den dönen tip (data.items) ile uyumlu olması yeterli */
  getDownloadLinks: (path: string) => Promise<{ success?: boolean; data?: { items?: Array<{ name?: string | null; url?: string | null }> | null }; message?: string }>;
};

/**
 * Entry'ye göre indirilecek link listesini döndürür.
 * - File: Sadece getFileById kullanılır; versions içinde zaten url ve name var, getDownloadLinks çağrılmaz.
 * - Project/Folder: getDownloadLinks(path) ile items alınır.
 */
export async function getDownloadItemsForEntry(
  entry: { id?: number; path?: string; entryType?: string },
  fetchers: DownloadItemsFetchers
): Promise<DownloadLinkItem[]> {
  if (!entry) return [];
  if (entry.entryType === 'File' && entry.id != null) {
    const res = await fetchers.getFileById(entry.id);
    if (!res?.success || !res?.data?.versions?.length) return [];
    const fileName = res.data.name || 'file';
    return res.data.versions
      .filter((v: { fileUrl?: string | null }) => v?.fileUrl)
      .map((v: { name?: string | null; fileUrl?: string | null }, i: number) => ({
        name: v.name ? `${fileName}#${v.name}` : `${fileName}#v${i + 1}`,
        url: v.fileUrl ?? null,
      }));
  }
  if (entry.path) {
    const res = await fetchers.getDownloadLinks(entry.path);
    if (!res?.success || !('data' in res) || !res.data?.items?.length) return [];
    return res.data.items;
  }
  return [];
}

/**
 * Tüm linkleri paralel fetch edip, indirmeleri kısa gecikmeyle tetikler (tarayıcı sıraya sokmasın diye).
 */
export async function triggerDownloadsFromItems(items: DownloadLinkItem[]): Promise<void> {
  const validItems = items.filter((item): item is DownloadLinkItem & { url: string } => !!item?.url);
  const fetchPromises = validItems.map(async (item) => {
    const response = await fetch(item.url, { credentials: 'omit' });
    const baseName = item.name || 'download';
    const ext = getExtensionFromUrl(item.url);
    const downloadFileName = ext ? `${baseName}.${ext}` : baseName;
    if (!response.ok) {
      console.error(`[Download] ${downloadFileName} başarısız:`, response.status, response.statusText);
      return null;
    }
    const blob = await response.blob();
    return { downloadFileName, blob };
  });
  const settled = await Promise.allSettled(fetchPromises);
  const blobs: { downloadFileName: string; blob: Blob }[] = [];
  settled.forEach((r) => {
    if (r.status === 'fulfilled' && r.value) blobs.push(r.value);
    else if (r.status === 'rejected') console.error(`[Download] hata:`, r.reason);
  });
  blobs.forEach(({ downloadFileName, blob }, index) => {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = downloadFileName;
    document.body.appendChild(link);
    setTimeout(() => {
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 2000);
    }, index * 80);
  });
}
